import React from "react";
import { useDispatch, useSelector } from "react-redux";
import parse from "html-react-parser";
import { useTheme } from "styled-components";

import { requestDesignUpdate } from "../../store/requests/thunks/requestDesignUpdate";

import {
  selectRegularRoofs,
  selectTinRoofs,
  selectSandwichRoofs,
} from "../../store/Options/selectRoofs";

import { getRoofName } from "../../helpers/roofs";

import OptionList from "../../components/OptionList/OptionList";
import RoofCompare from "../../components/RoofCompare/RoofCompare";
import Box from "@mui/material/Box";

const EXPAND_TIN_ROOF_OPTIONS = "expand_tin_roof_options";
const EXPAND_SANDWICH_ROOF_OPTIONS = "expand_sandwich_roof_options";

const RoofOptions = () => {
  const dispatch = useDispatch();

  const init = useSelector((state) => state.init);
  const Uterum = useSelector((state) => state.Uterum);
  const { assetBaseUrl, alias } = init;
  const regularRoofs = useSelector(selectRegularRoofs);
  const tinRoofs = useSelector(selectTinRoofs);
  const sandwichRoofs = useSelector(selectSandwichRoofs);
  const roofOptions = [];

  const theme = useTheme();

  regularRoofs &&
    regularRoofs.forEach((roof) => {
      const value = roof.Value;
      const label = getRoofName(roof, alias);

      // roofObject is the roof option set up with the correct key/value pairs, ready to be pushed into the roofOptions array
      const roofObject = {
        value,
        label,
        imgSrc: `${assetBaseUrl}/store/uterumsguide2016/roof/${value}.png`,
        imgAlt: label,
      };

      // Set which seasons should include which roof-options
      const optimalRoofOptions = ["ROOF_10", "ROOF_20"];
      const isolentRoofOptions = ["ROOF_32", "ROOF_50"];
      const isomaxRoofOptions = ["ROOF_50"];

      // Check season and which roofs that should be rendered based on it
      switch (Uterum.Season) {
        case "OPTIMAL":
          if (optimalRoofOptions.includes(value)) {
            roofOptions.push(roofObject);
          }
          break;
        case "ISOLENT":
          if (isolentRoofOptions.includes(value)) {
            roofOptions.push(roofObject);
          }
          break;
        case "ISOMAX":
          if (isomaxRoofOptions.includes(value)) {
            roofOptions.push(roofObject);
          }
          break;
        default:
          break;
      }
    });

  if (tinRoofs && tinRoofs.length) {
    roofOptions.push({
      value: EXPAND_TIN_ROOF_OPTIONS,
      label: `${alias.bfcTinRoof}`,
      imgSrc: `${assetBaseUrl}/store/uterumsguide2016/roof/ROOF_TIN.png`,
      imgAlt: `${alias.bfcTinRoof}`,
    });
  }

  // SandwhichRoofs are not available on Nordrum channels (i.e. 7 and 8)
  if (
    init.channelId !== "7" &&
    init.channelId !== "8" &&
    sandwichRoofs &&
    sandwichRoofs.length
  ) {
    roofOptions.push({
      value: EXPAND_SANDWICH_ROOF_OPTIONS,
      label: `${alias.bfcSandwichRoof}`,
      imgSrc: `${assetBaseUrl}/store/uterumsguide2016/roof/ROOF_SANDWICH_SILVER.png`,
      imgAlt: `${alias.bfcSandwichRoof}`,
    });
  }

  const expandedTinRoofOptions =
    tinRoofs &&
    tinRoofs.map((roof) => {
      const value = roof.Value;
      const label = getRoofName(roof, alias);

      return {
        value,
        label,
        imgSrc: `${assetBaseUrl}/store/uterumsguide2016/roof/${value}.png`,
        imgAlt: label,
      };
    });

  const expandedSandwichRoofOptions =
    sandwichRoofs &&
    sandwichRoofs.map((roof) => {
      const value = roof.Value;
      const label = getRoofName(roof, alias);

      return {
        value,
        label,
        imgSrc: `${assetBaseUrl}/store/uterumsguide2016/roof/${value}.png`,
        imgAlt: label,
      };
    });

  const activeRoofOption = Uterum.Roof.includes("ROOF_TIN")
    ? EXPAND_TIN_ROOF_OPTIONS
    : Uterum.Roof.includes("ROOF_SANDWICH")
    ? EXPAND_SANDWICH_ROOF_OPTIONS
    : Uterum.Roof;

  return (
    <Box mb={4}>
      <OptionList
        renderAs="thumbnails"
        desktopRenderWidth="75%"
        background={theme.light}
        hideBorder={true}
        name="Roof"
        label={alias.bfcChooseRoof}
        tooltipContent={<RoofCompare />}
        description={alias.bfcStep4RoofOptionDescription}
        options={roofOptions}
        activeOptionValueFromParent={activeRoofOption}
        onChange={(value) => {
          dispatch(
            requestDesignUpdate({
              uterumProperties: {
                Roof:
                  value === EXPAND_TIN_ROOF_OPTIONS
                    ? tinRoofs[0].Value
                    : value === EXPAND_SANDWICH_ROOF_OPTIONS
                    ? sandwichRoofs[0].Value
                    : value,
              },
            })
          );
        }}
      />

      {Uterum.Roof.includes("ROOF_TIN") && (
        <>
          <OptionList
            renderAs="thumbnails"
            desktopRenderWidth="75%"
            name="Tin_Roof"
            background={theme.light}
            hideBorder={true}
            label={alias.bfcTinRoofColor}
            tooltipContent={
              alias.tooltip_tin_roof && parse(alias.tooltip_tin_roof)
            }
            options={expandedTinRoofOptions}
            activeOptionValueFromParent={Uterum.Roof}
            onChange={(value) => {
              dispatch(
                requestDesignUpdate({
                  uterumProperties: {
                    Roof: value,
                  },
                })
              );
            }}
          />
        </>
      )}

      {Uterum.Roof.includes("ROOF_SANDWICH") && (
        <>
          <OptionList
            renderAs="thumbnails"
            desktopRenderWidth="75%"
            name="Sandwich_Roof"
            background={theme.light}
            hideBorder={true}
            label={alias.bfcSandwichRoofColor}
            tooltipContent={
              alias.tooltip_sandwich_roof && parse(alias.tooltip_sandwich_roof)
            }
            options={expandedSandwichRoofOptions}
            activeOptionValueFromParent={Uterum.Roof}
            onChange={(value) => {
              dispatch(
                requestDesignUpdate({
                  uterumProperties: {
                    Roof: value,
                  },
                })
              );
            }}
          />
        </>
      )}
    </Box>
  );
};

export default RoofOptions;
